import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import RouteDetailsModal from './RouteDetailsModal';
import CircularLoader from './CircularLoader';
import { readImpersonationId, withImpersonationHeader } from '../utils/impersonation';

interface DynamicRoute {
  id: string;
  name: string;
  description: string;
  path: string;
  methods: string[];
  handler_code: string;
  auth_required: boolean;
  created_at: string;
  last_used: string | null;
  usage_count: number;
  owner_user_id: string;
}

interface RouteHealth {
  status: 'healthy' | 'degraded' | 'down';
  uptime_percent: number;
  consecutive_failures: number;
  avg_latency_ms: number;
  error_rate_percent: number;
  last_hour_requests: number;
}

const RoutesDisplay: React.FC = () => {
  const { session, impersonatedUserId } = useAuth();
  const [routes, setRoutes] = useState<DynamicRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<DynamicRoute | null>(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState<string | null>(null);
  const [routeHealth, setRouteHealth] = useState<Record<string, RouteHealth>>({});

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    field: keyof DynamicRoute | 'health_status' | 'last_hour_requests';
    direction: 'asc' | 'desc';
  }>({
    field: 'created_at',
    direction: 'desc' // Show newest routes first by default
  });

  const latestRequestRef = useRef(0);

  // Filtering state
  const [filters, setFilters] = useState({
    search: '',
    healthStatus: [] as string[], // ['healthy', 'degraded', 'down']
    authRequired: null as boolean | null, // null = all, true = auth required, false = public
    usageRange: [0, Infinity] as [number, number], // [min, max] usage count
    showFilters: false
  });

  // Sorting functions
  const sortRoutes = (routesToSort: DynamicRoute[], field: typeof sortConfig.field, direction: 'asc' | 'desc'): DynamicRoute[] => {
    return [...routesToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Handle special fields that come from routeHealth
      if (field === 'health_status') {
        // Priority order: healthy > degraded > down > unknown
        const healthPriority = { 'healthy': 3, 'degraded': 2, 'down': 1 };
        aValue = healthPriority[routeHealth[a.id]?.status as keyof typeof healthPriority] || 0;
        bValue = healthPriority[routeHealth[b.id]?.status as keyof typeof healthPriority] || 0;
      } else if (field === 'last_hour_requests') {
        aValue = routeHealth[a.id]?.last_hour_requests || 0;
        bValue = routeHealth[b.id]?.last_hour_requests || 0;
      } else {
        // Handle regular route fields
        aValue = a[field as keyof DynamicRoute];
        bValue = b[field as keyof DynamicRoute];
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // String comparison (case-insensitive)
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return direction === 'asc' ? comparison : -comparison;
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        // Numeric comparison
        const comparison = aValue - bValue;
        return direction === 'asc' ? comparison : -comparison;
      } else if (aValue instanceof Date || bValue instanceof Date) {
        // Date comparison
        const aTime = new Date(aValue).getTime();
        const bTime = new Date(bValue).getTime();
        const comparison = aTime - bTime;
        return direction === 'asc' ? comparison : -comparison;
      } else {
        // Fallback to string comparison
        const aStr = String(aValue || '');
        const bStr = String(bValue || '');
        const comparison = aStr.toLowerCase().localeCompare(bStr.toLowerCase());
        return direction === 'asc' ? comparison : -comparison;
      }
    });
  };

  const handleSort = (field: typeof sortConfig.field) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filtering functions
  const filterRoutes = (routesToFilter: DynamicRoute[]): DynamicRoute[] => {
    return routesToFilter.filter(route => {
      // Search filter (name + description)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesName = route.name.toLowerCase().includes(searchLower);
        const matchesDescription = route.description?.toLowerCase().includes(searchLower) || false;
        if (!matchesName && !matchesDescription) return false;
      }

      // Health status filter
      if (filters.healthStatus.length > 0) {
        const routeHealthStatus = routeHealth[route.id]?.status || 'unknown';
        if (!filters.healthStatus.includes(routeHealthStatus)) return false;
      }

      // Auth required filter
      if (filters.authRequired !== null) {
        if (route.auth_required !== filters.authRequired) return false;
      }

      // Usage range filter
      if (route.usage_count < filters.usageRange[0] || route.usage_count > filters.usageRange[1]) {
        return false;
      }

      return true;
    });
  };

  // Handle filter changes
  const updateFilter = (key: keyof typeof filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      search: '',
      healthStatus: [],
      authRequired: null,
      usageRange: [0, Infinity],
      showFilters: false
    });
  };

  const toggleHealthFilter = (status: string) => {
    setFilters(prev => ({
      ...prev,
      healthStatus: prev.healthStatus.includes(status)
        ? prev.healthStatus.filter(s => s !== status)
        : [...prev.healthStatus, status]
    }));
  };

  // Debounced search for performance
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Get filtered and sorted routes using useMemo for performance
  const filteredAndSortedRoutes = React.useMemo(() => {
    const filtersWithDebounce = { ...filters, search: debouncedSearch };
    const filtered = routes.filter(route => {
      // Search filter (name + description)
      if (filtersWithDebounce.search) {
        const searchLower = filtersWithDebounce.search.toLowerCase();
        const matchesName = route.name.toLowerCase().includes(searchLower);
        const matchesDescription = route.description?.toLowerCase().includes(searchLower) || false;
        if (!matchesName && !matchesDescription) return false;
      }

      // Health status filter
      if (filtersWithDebounce.healthStatus.length > 0) {
        const routeHealthStatus = routeHealth[route.id]?.status || 'unknown';
        if (!filtersWithDebounce.healthStatus.includes(routeHealthStatus)) return false;
      }

      // Auth required filter
      if (filtersWithDebounce.authRequired !== null) {
        if (route.auth_required !== filtersWithDebounce.authRequired) return false;
      }

      // Usage range filter
      if (route.usage_count < filtersWithDebounce.usageRange[0] || route.usage_count > filtersWithDebounce.usageRange[1]) {
        return false;
      }

      return true;
    });
    return sortRoutes(filtered, sortConfig.field, sortConfig.direction);
  }, [routes, debouncedSearch, filters.healthStatus, filters.authRequired, filters.usageRange, sortConfig.field, sortConfig.direction, routeHealth]);

  const getAuthHeaders = (): HeadersInit => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    return withImpersonationHeader(headers);
  };

  const loadCombinedRoutesData = useCallback(async () => {
    if (!session?.access_token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    const requestId = ++latestRequestRef.current;
    const impersonationSnapshot = readImpersonationId();

    try {
      setLoading(true);
      const response = await fetch(config.getApiUrl('/routes/combined'), {
        headers: getAuthHeaders()
      });
      const currentImpersonation = readImpersonationId();
      const isStale = requestId !== latestRequestRef.current || impersonationSnapshot !== currentImpersonation;

      if (response.ok) {
        const data = await response.json();
        if (isStale) {
          console.debug('Ignoring stale routes response due to newer request or impersonation switch');
          return;
        }

        setRoutes(data.routes || []);

        const healthMap: Record<string, RouteHealth> = {};
        data.metrics?.routes?.forEach((routeMetrics: any) => {
          healthMap[routeMetrics.route_id] = {
            status: routeMetrics.health.status,
            uptime_percent: routeMetrics.health.uptime_percent,
            consecutive_failures: routeMetrics.health.consecutive_failures,
            avg_latency_ms: routeMetrics.performance.avg_latency_ms,
            error_rate_percent: routeMetrics.errors.error_rate_percent,
            last_hour_requests: routeMetrics.volume.last_hour
          };
        });
        setRouteHealth(healthMap);

        setError(null);
      } else if (!isStale) {
        if (response.status === 401) {
          setError('Authentication failed - please log in again');
        } else {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          if (requestId !== latestRequestRef.current || impersonationSnapshot !== readImpersonationId()) {
            return;
          }
          setError(errorData.detail || 'Failed to load routes');
        }
      }
    } catch (err) {
      console.error('Error loading combined routes data:', err);
      if (requestId === latestRequestRef.current && impersonationSnapshot === readImpersonationId()) {
        setError('Network error - failed to load routes');
      }
    } finally {
      const currentImpersonation = readImpersonationId();
      if (requestId === latestRequestRef.current && impersonationSnapshot === currentImpersonation) {
        setLoading(false);
      }
    }
  }, [session?.access_token, impersonatedUserId]);

  const confirmDeleteRoute = (routeId: string) => {
    setRouteToDelete(routeId);
  };

  const deleteRoute = async () => {
    if (!routeToDelete || !session?.access_token) {
      setError('Authentication required');
      return;
    }

    try {
      const response = await fetch(config.getApiUrl(`/routes/${routeToDelete}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setRoutes(prev => prev.filter(route => route.id !== routeToDelete));
        if (selectedRoute?.id === routeToDelete) {
          setSelectedRoute(null);
          setShowCodeModal(false);
        }
        setRouteToDelete(null);
      } else if (response.status === 401) {
        setError('Authentication failed - please log in again');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        setError(errorData.detail || 'Failed to delete route');
      }
    } catch (err) {
      console.error('Error deleting route:', err);
      setError('Network error - failed to delete route');
    }
  };

  const cancelDelete = () => {
    setRouteToDelete(null);
  };


  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };



  const getHealthIcon = (status: string): string => {
    switch (status) {
      case 'healthy': return '●';
      case 'degraded': return '●';
      case 'down': return '●';
      default: return '●';
    }
  };

  const getHealthColor = (status: string): string => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'degraded': return 'text-yellow-400';
      case 'down': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const [selectedRouteCurlExamples, setSelectedRouteCurlExamples] = useState<Record<string, string>>({});

  const showRouteCode = async (route: DynamicRoute) => {
    setSelectedRoute(route);
    setShowCodeModal(true);
    
    // Fetch curl examples for the selected route
    try {
      const curlExamples = await generateCurlExamples(route);
      setSelectedRouteCurlExamples(curlExamples);
    } catch (error) {
      console.error('Error fetching curl examples for modal:', error);
      setSelectedRouteCurlExamples({});
    }
  };

  const closeCodeModal = () => {
    setShowCodeModal(false);
    setSelectedRoute(null);
    setSelectedRouteCurlExamples({});
  };

  const [curlCache, setCurlCache] = useState<Record<string, string>>({});
  const [curlExamplesCache, setCurlExamplesCache] = useState<Record<string, Record<string, string>>>({});

  const generateCurlCommand = async (route: DynamicRoute): Promise<string> => {
    // Check cache first
    if (curlCache[route.id]) {
      return curlCache[route.id];
    }

    try {
      // Call the backend endpoint to get curl examples with proper DynamoDB examples
      const response = await fetch(config.getApiUrl(`/routes/${route.id}/curl`), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        const curlCommand = data.curl_examples?.POST || 'No curl example available';
        
        // Cache the result
        setCurlCache(prev => ({ ...prev, [route.id]: curlCommand }));
        
        return curlCommand;
      } else {
        console.error('Failed to fetch curl example from backend');
        return 'Failed to generate curl example';
      }
    } catch (error) {
      console.error('Error fetching curl example:', error);
      return 'Error generating curl example';
    }
  };

  const generateCurlExamples = async (route: DynamicRoute): Promise<Record<string, string>> => {
    // Check cache first
    if (curlExamplesCache[route.id]) {
      return curlExamplesCache[route.id];
    }

    try {
      // Call the backend endpoint to get full curl examples with proper parameters
      const response = await fetch(config.getApiUrl(`/routes/${route.id}/curl`), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        const curlExamples = data.curl_examples || {};
        
        // Cache the result
        setCurlExamplesCache(prev => ({ ...prev, [route.id]: curlExamples }));
        
        return curlExamples;
      } else {
        console.error('Failed to fetch curl examples from backend');
        return {};
      }
    } catch (error) {
      console.error('Error fetching curl examples:', error);
      return {};
    }
  };

  const copyCurlToClipboard = async (route: DynamicRoute, buttonRef?: HTMLButtonElement) => {
    try {
      const curlCommand = await generateCurlCommand(route);
      await navigator.clipboard.writeText(curlCommand);
      
      // Show a temporary success message
      if (buttonRef) {
        const originalText = buttonRef.textContent;
        buttonRef.textContent = '✅ Copied!';
        setTimeout(() => {
          buttonRef.textContent = originalText;
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy cURL command:', err);
      alert('Failed to copy cURL command to clipboard');
    }
  };

  // Component to handle async curl display
  const CurlDisplay: React.FC<{ route: DynamicRoute; generateCurlCommand: (route: DynamicRoute) => Promise<string> }> = 
    ({ route, generateCurlCommand }) => {
    const [curlCommand, setCurlCommand] = useState<string>('Loading curl example...');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadCurl = async () => {
        try {
          setLoading(true);
          const command = await generateCurlCommand(route);
          setCurlCommand(command);
        } catch (error) {
          setCurlCommand('Error loading curl example');
        } finally {
          setLoading(false);
        }
      };
      
      loadCurl();
    }, [route.id]);

    if (loading) {
      return (
        <div className="flex items-center justify-center py-4">
          <div className="w-6 h-6 border-2 border-zinc-600 border-t-green-400 rounded-full animate-spin"></div>
          <span className="ml-2 text-zinc-400">Loading curl example...</span>
        </div>
      );
    }

    return (
      <pre className="text-sm text-green-300 font-mono leading-relaxed whitespace-pre-wrap break-all">
        <code>{curlCommand}</code>
      </pre>
    );
  };

  useEffect(() => {
    loadCombinedRoutesData();
  }, [session?.access_token, impersonatedUserId, loadCombinedRoutesData]);

  if (loading) {
    return (
      <div className="flex flex-col h-screen w-full bg-zinc-950">
        <div className="flex-1 p-5">
          <CircularLoader size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-screen w-full bg-zinc-950">
        <div className="flex-1 p-5">
          <div className="flex flex-col items-center justify-center h-80 text-center">
            <span className="text-red-500 text-lg">❌ {error}</span>
            <button 
              className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200 mt-4"
              onClick={loadCombinedRoutesData}
            >
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full bg-zinc-950">
      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-8">
        {routes.length === 0 ? (
          <div className="min-h-96 flex items-center justify-center">
            <div className="text-center bg-zinc-900/50 rounded-xl p-10 border border-zinc-800">
              <h3 className="text-xl font-semibold text-zinc-200 mb-3">No Dynamic Routes Found</h3>
              <p className="text-zinc-400 leading-relaxed max-w-md">You haven't created any dynamic routes yet. Create routes through the chat interface to see them here.</p>
            </div>
          </div>
        ) : (
          <div className="w-full pb-8">
            {/* Header with refresh button */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-zinc-200">Dynamic Routes</h2>
              <button 
                className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200 disabled:text-zinc-600"
                onClick={loadCombinedRoutesData}
                disabled={loading}
              >
                🔄 Refresh
              </button>
            </div>
            
            {/* Stats Section */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 border border-slate-600/50 rounded-xl p-6 text-center backdrop-blur-sm">
                <div className="text-3xl font-bold text-white mb-2">{routes.length}</div>
                <div className="text-sm text-slate-300 font-medium">Total Routes</div>
              </div>
              <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 border border-slate-600/50 rounded-xl p-6 text-center backdrop-blur-sm">
                <div className="text-3xl font-bold text-white mb-2">
                  {routes.reduce((sum, r) => sum + r.usage_count, 0).toLocaleString()}
                </div>
                <div className="text-sm text-slate-300 font-medium">Total API Calls</div>
              </div>
            </div>

            {/* Combined Controls Panel */}
            <div className="mb-8">
              <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-xl p-6 space-y-6">
                {/* Top Row: Search and Action Buttons */}
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-12 pr-6 py-3 bg-zinc-900/60 border border-zinc-700/50 rounded-xl text-sm text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                      placeholder="Search routes by name or description..."
                      value={filters.search}
                      onChange={(e) => updateFilter('search', e.target.value)}
                    />
                    {filters.search && (
                      <button
                        onClick={() => updateFilter('search', '')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-zinc-400 hover:text-zinc-200"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateFilter('showFilters', !filters.showFilters)}
                      className={`px-4 py-3 bg-zinc-900/60 border border-zinc-700/50 rounded-xl text-sm transition-all duration-200 hover:bg-zinc-800/60 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                        filters.showFilters ? 'bg-zinc-800/60 border-blue-500/50' : ''
                      }`}
                    >
                      <svg className="h-4 w-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>

                    {(filters.search || filters.healthStatus.length > 0 || filters.authRequired !== null || filters.usageRange[0] > 0 || filters.usageRange[1] < Infinity) && (
                      <button
                        onClick={clearAllFilters}
                        className="px-4 py-3 bg-red-600/20 border border-red-500/30 rounded-xl text-sm text-red-300 hover:bg-red-600/30 transition-all duration-200"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>

                {/* Sort Controls */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-zinc-300">Sort by:</span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleSort('name')}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        sortConfig.field === 'name'
                          ? 'bg-blue-600/20 border border-blue-500/30 text-blue-300'
                          : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/60'
                      }`}
                    >
                      Name
                      {sortConfig.field === 'name' && (
                        <span className="text-blue-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => handleSort('created_at')}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        sortConfig.field === 'created_at'
                          ? 'bg-blue-600/20 border border-blue-500/30 text-blue-300'
                          : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/60'
                      }`}
                    >
                      Created
                      {sortConfig.field === 'created_at' && (
                        <span className="text-blue-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => handleSort('usage_count')}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        sortConfig.field === 'usage_count'
                          ? 'bg-blue-600/20 border border-blue-500/30 text-blue-300'
                          : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/60'
                      }`}
                    >
                      Usage
                      {sortConfig.field === 'usage_count' && (
                        <span className="text-blue-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => handleSort('health_status')}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        sortConfig.field === 'health_status'
                          ? 'bg-blue-600/20 border border-blue-500/30 text-blue-300'
                          : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/60'
                      }`}
                    >
                      Health
                      {sortConfig.field === 'health_status' && (
                        <span className="text-blue-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => handleSort('last_hour_requests')}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        sortConfig.field === 'last_hour_requests'
                          ? 'bg-blue-600/20 border border-blue-500/30 text-blue-300'
                          : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/60'
                      }`}
                    >
                      Recent Activity
                      {sortConfig.field === 'last_hour_requests' && (
                        <span className="text-blue-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Advanced Filters Panel */}
                {filters.showFilters && (
                  <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-3">Health Status</label>
                        <div className="space-y-3">
                          {['healthy', 'degraded', 'down'].map(status => (
                            <label key={status} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={filters.healthStatus.includes(status)}
                                onChange={() => toggleHealthFilter(status)}
                                className="rounded border-zinc-600 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                              />
                              <span className={`ml-2 text-sm capitalize ${
                                status === 'healthy' ? 'text-green-400' :
                                status === 'degraded' ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {status}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-3">Authentication</label>
                        <select
                          value={filters.authRequired === null ? 'all' : filters.authRequired ? 'required' : 'public'}
                          onChange={(e) => {
                            const value = e.target.value === 'all' ? null : e.target.value === 'required';
                            updateFilter('authRequired', value);
                          }}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50"
                        >
                          <option value="all">All Routes</option>
                          <option value="required">Auth Required</option>
                          <option value="public">Public</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-3">Minimum Usage</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="e.g., 100"
                          value={filters.usageRange[0] === 0 ? '' : filters.usageRange[0]}
                          onChange={(e) => {
                            const min = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                            updateFilter('usageRange', [min, filters.usageRange[1]]);
                          }}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Results Summary */}
            {(filters.search || filters.healthStatus.length > 0 || filters.authRequired !== null || filters.usageRange[0] > 0) && (
              <div className="mb-4 text-center">
                <span className="text-sm text-zinc-400">
                  Showing {filteredAndSortedRoutes.length} of {routes.length} routes
                  {filters.search && (
                    <span className="ml-2 text-blue-400">
                      matching "{filters.search}"
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Routes Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 px-1 py-1">
              {filteredAndSortedRoutes.length > 0 ? filteredAndSortedRoutes.map((route) => (
                <div key={route.id} className="group relative bg-gradient-to-br from-zinc-900/80 via-zinc-900/60 to-zinc-800/40 border border-zinc-700/50 rounded-xl p-6 transition-all duration-300 hover:border-zinc-500/60 hover:shadow-lg hover:shadow-zinc-900/20 hover:scale-[1.02] backdrop-blur-sm" data-route-id={route.id}>
                  <div className="flex items-start justify-between mb-4 gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <h3
                          className="text-lg font-bold text-white leading-tight tracking-tight truncate flex-1 min-w-0"
                          title={route.name}
                        >
                          {route.name}
                        </h3>
                        {routeHealth[route.id] && (
                          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${
                            routeHealth[route.id].status === 'healthy'
                              ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300'
                              : routeHealth[route.id].status === 'degraded'
                              ? 'bg-amber-500/20 border-amber-500/30 text-amber-300'
                              : 'bg-red-500/20 border-red-500/30 text-red-300'
                          }`}>
                            {getHealthIcon(routeHealth[route.id].status)}
                            <span className="capitalize">{routeHealth[route.id].status}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2.5">
                        {routeHealth[route.id] && (
                          <>
                            <div className="flex items-center gap-2 bg-slate-800/60 backdrop-blur-sm border border-slate-600/30 text-slate-200 text-xs font-medium px-3 py-1.5 rounded-lg">
                              <span className="text-blue-400">⚡</span>
                              <span>{routeHealth[route.id].avg_latency_ms}ms</span>
                            </div>
                            <div className="flex items-center gap-2 bg-slate-800/60 backdrop-blur-sm border border-slate-600/30 text-slate-200 text-xs font-medium px-3 py-1.5 rounded-lg">
                              <span className="text-green-400">📊</span>
                              <span>{routeHealth[route.id].uptime_percent}% uptime</span>
                            </div>
                            {routeHealth[route.id].error_rate_percent > 0 && (
                              <div className="flex items-center gap-2 bg-red-900/40 border border-red-600/40 text-red-200 text-xs font-medium px-3 py-1.5 rounded-lg">
                                <span className="text-red-400">⚠️</span>
                                <span>{routeHealth[route.id].error_rate_percent}% errors</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 opacity-60 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                      <button
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-600/60 border border-slate-600/50 hover:border-slate-500/60 rounded-lg transition-all duration-200 hover:scale-105"
                        onClick={() => showRouteCode(route)}
                        title="View handler code & copy cURL"
                      >
                        <span>👁️</span>
                        <span>View</span>
                      </button>
                      <button
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-300 hover:text-red-200 bg-red-900/30 hover:bg-red-800/40 border border-red-700/50 hover:border-red-600/60 rounded-lg transition-all duration-200 hover:scale-105"
                        onClick={() => confirmDeleteRoute(route.id)}
                        title="Delete route"
                      >
                        <span>🗑️</span>
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>

                  {route.description && (
                    <div className="text-slate-300 text-sm leading-relaxed mb-4 p-3 bg-slate-800/30 border border-slate-700/30 rounded-lg backdrop-blur-sm break-words">
                      {route.description}
                    </div>
                  )}

                  <div className="border-t border-slate-700/50 pt-4 mt-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-xs text-slate-400 mb-1">Created</div>
                        <div className="text-sm font-semibold text-slate-200">{formatDate(route.created_at)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 mb-1">Total Usage</div>
                        <div className="text-sm font-semibold text-slate-200">{route.usage_count} calls</div>
                      </div>
                      {routeHealth[route.id] ? (
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Last Hour</div>
                          <div className="text-sm font-semibold text-slate-200">{routeHealth[route.id].last_hour_requests} calls</div>
                        </div>
                      ) : (
                        <div className="opacity-50">
                          <div className="text-xs text-slate-400 mb-1">Last Hour</div>
                          <div className="text-sm font-semibold text-slate-200">-</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-12">
                  <div className="text-center bg-zinc-900/50 rounded-xl p-10 border border-zinc-800">
                    <h3 className="text-xl font-semibold text-zinc-200 mb-3">No Routes Found</h3>
                    <p className="text-zinc-400 leading-relaxed max-w-md mx-auto">
                      {filters.search || filters.healthStatus.length > 0 || filters.authRequired !== null || filters.usageRange[0] > 0
                        ? "No routes match your current filters. Try adjusting your search criteria."
                        : "You haven't created any dynamic routes yet. Create routes through the chat interface to see them here."
                      }
                    </p>
                    {(filters.search || filters.healthStatus.length > 0 || filters.authRequired !== null || filters.usageRange[0] > 0) && (
                      <button
                        onClick={clearAllFilters}
                        className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors duration-200"
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Code Modal */}
      {showCodeModal && selectedRoute && (
        <RouteDetailsModal
          isOpen={true}
          onClose={closeCodeModal}
          routeInfo={selectedRoute}
          curlExamples={selectedRouteCurlExamples}
          isNewRoute={false}
        />
      )}

      {/* Delete Confirmation Modal */}
      {routeToDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5" onClick={cancelDelete}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <h3 className="text-lg font-semibold text-zinc-200">⚠️ Confirm Delete</h3>
              <button 
                className="text-zinc-400 hover:text-zinc-200 text-2xl transition-colors duration-200" 
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <p className="text-zinc-300 mb-4">Are you sure you want to delete this route?</p>
              <p className="text-zinc-200 font-semibold mb-4">{routes.find(r => r.id === routeToDelete)?.name}</p>
              <p className="text-amber-400 text-sm mb-6">This action cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button 
                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg transition-colors duration-200"
                  onClick={cancelDelete}
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                  onClick={deleteRoute}
                >
                  Delete Route
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutesDisplay;
